import { Button, Card, H3, Intent, Menu, MenuItem } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import { Laptop } from '@carbon/icons-react';
import classNames from 'classnames';
import { getPortalSdk } from 'portal-sdk';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'vet-bones/components';
import { emptyObject } from 'vet-bones/constants';
import { Icon } from 'vet-bones/design-system/components';
import { isOnPremise, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { EditableLabel } from 'src/components/EditableLabel';
import { withConfirmation } from 'src/components/withConfirmation';
import {
  TEST_IDS,
  getProjectCardTestId,
  getProjectMenuButtonTestId,
  getProjectTitleTestId,
} from 'src/constants/testIds';
import { flushPendingProjectSave } from 'src/store/cloudProjectMiddleware';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { duplicateProjectToCloud } from 'src/utils/duplicate/duplicateProjectToCloud';
import { decodeCloudStorageProject } from 'src/utils/exportProject/encodeProject';
import { formatDateFromMs } from 'src/utils/formatDateFromMs';
import { generateDateTimeId } from 'src/utils/generateId';
import {
  CloudProject,
  getCloudProjectListQueryKey,
} from 'src/utils/hooks/useCloudStorageProjects';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { queryClient } from 'src/utils/queryClient';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

type ProjectCardProps = {
  idx: number | undefined;
  hidden?: boolean;
  project: Project | CloudProject;
};

export const ProjectCard: React.VFC<ProjectCardProps> = ({
  idx,
  hidden,
  project,
}) => {
  const dispatch = useReduxDispatch();
  const isLaunchpad = isOnPremise();

  const portalUser = usePortalUser();
  const isCloudStorageEnabled = portalUser.data?.is_designer_storage_enabled;

  const [isHovered, setIsHovered] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const isCloudProject = 'isCloudStorageProject' in project;
  const handleMouseEnter = React.useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const handleMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  const handleRename = async (name: string) => {
    await dispatch(async (wrappedDispatch) => {
      wrappedDispatch(
        projectsActionCreators.clearedProjectVoiceboxEvaluation({
          id: project.id,
        })
      );
    });
    // must wait for previous action middleware to finish completely
    // before renaming project to avoid cloud sync issues
    dispatch(projectsActionCreators.renamedProject({ id: project.id, name }));
  };

  const handleDuplicateProject = React.useCallback(async () => {
    if (isCloudProject) {
      await flushPendingProjectSave(project.id);

      const portalSdk = getPortalSdk();
      const cloudProject = await portalSdk?.getDesignerProject(project.id);
      if (cloudProject?.content) {
        try {
          const data = decodeCloudStorageProject(cloudProject.content);
          const project = Object.values(
            data?.projects?.projectsById || emptyObject
          )[0] as Project | undefined;

          if (!project) {
            return;
          }

          const newProject = duplicateProject(project, {
            id: cloudProject.id,
            name: `${cloudProject.name} Copy`,
            isCloudProject: true,
            dateLastUpdated: new Date().getTime(),
            dateCreated: new Date().getTime(),
            // do not copy voicebox related data
            voiceboxConversationId: undefined,
            voiceboxMessageHistory: undefined,
            voiceboxEvaluatedCompetencyQuestions: undefined,
            voiceboxEvaluatedSummary: undefined,
          });

          await duplicateProjectToCloud(newProject);
        } catch {
          console.error('Error decoding cloud project');
        }
      }
    } else {
      const copyId = generateDateTimeId();
      dispatch(
        projectsActionCreators.duplicatedLocalProject(project.id, copyId)
      );
    }
  }, [isCloudProject, project, dispatch]);

  const handleRemoveProject = React.useCallback(() => {
    const restoreAction = isCloudProject
      ? projectsActionCreators.restoredCloudProject({
          id: project.id,
          name: project.name,
        })
      : projectsActionCreators.restoredProject({
          id: project.id,
          name: project.name,
        });

    dispatch(
      projectsActionCreators.archivedProject<typeof restoreAction>({
        id: project.id,
        name: project.name,
        restoreAction,
      })
    );
  }, [dispatch, project, isCloudProject]);

  const handleMigrateProject = React.useCallback(() => {
    withConfirmation({
      message: copy.components.home.cards.confirmationDialogContent(
        isCloudProject,
        isLaunchpad
      ),
      onConfirmationSuccess: async () => {
        if (isCloudProject) {
          const portalSdk = getPortalSdk();
          const copyId = generateDateTimeId();
          dispatch(
            projectsActionCreators.migratedCloudProjectToLocal(
              project.id,
              copyId,
              project.name
            )
          );
          const removedCloudId = await portalSdk?.archiveDesignerProject(
            project.id
          );
          if (removedCloudId) {
            queryClient.refetchQueries(getCloudProjectListQueryKey());
          }
        } else {
          const newId = await duplicateProjectToCloud(project);
          if (newId) {
            dispatch(
              projectsActionCreators.migratedLocalProjectToCloud(
                project.id,
                project.name
              )
            );
          }
        }
      },
      yesButtonIntent: Intent.PRIMARY,
      noButtonOutlined: true,
    });
  }, [isCloudProject, project, dispatch, isLaunchpad]);

  const formattedDateString = React.useMemo(() => {
    return formatDateFromMs(project.dateLastUpdated);
  }, [project.dateLastUpdated]);

  return (
    <Link
      className={classNames('sd-link-reset', {
        'sd-display-none-important': hidden,
      })}
      data-testid={getProjectCardTestId(project.id, idx)}
      to={getProjectRoute(project.id)}
    >
      <Card
        className="sd-project-card"
        interactive
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="sd-project-card__header">
          <H3
            className="sd-project-card__name"
            data-testid={getProjectTitleTestId(project.id, idx)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <span
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              <EditableLabel onSetValue={handleRename} value={project.name} />
            </span>
            {isCloudStorageEnabled && !isCloudProject ? (
              <Tooltip
                content={copy.components.home.cards.projectStoredLocally}
              >
                <Icon IconComponent={Laptop} size="md" />
              </Tooltip>
            ) : null}
          </H3>
          <Popover2
            content={
              <Menu>
                <MenuItem
                  data-testid={TEST_IDS.projectMenu.duplicateItem}
                  onClick={handleDuplicateProject}
                  text={copy.components.home.cards.duplicateMenuItem}
                />
                {isCloudStorageEnabled ? (
                  <MenuItem
                    data-testid={TEST_IDS.projectMenu.migrateItem}
                    onClick={handleMigrateProject}
                    text={
                      isCloudProject
                        ? copy.components.home.cards.storeLocallyMenuItem
                        : copy.components.home.cards.storeCloudMenuItem(
                            isLaunchpad
                          )
                    }
                  />
                ) : null}
                <MenuItem
                  data-testid={TEST_IDS.projectMenu.deleteItem}
                  onClick={handleRemoveProject}
                  text={copy.components.home.cards.deleteMenuItem}
                />
              </Menu>
            }
            isOpen={isMenuOpen}
            minimal
            onClose={(e) => {
              // react testing library does not provide event here
              if (e) {
                // prevent triggering card link when clicking menu items
                e.stopPropagation();
              }
              setIsMenuOpen(false);
            }}
            targetProps={{
              onClick: (e) => {
                // prevent triggering parent interaction
                e.preventDefault();
              },
            }}
          >
            <Button
              active={isMenuOpen}
              data-testid={getProjectMenuButtonTestId(project.id, idx)}
              icon={isHovered || isMenuOpen ? IconNames.MORE : IconNames.BLANK}
              minimal
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
              }}
            />
          </Popover2>
        </div>
        <div
          className="sd-project-card__update-date"
          data-testid={TEST_IDS.projectCard.updateDate}
        >
          Last updated: {formattedDateString}
        </div>
      </Card>
    </Link>
  );
};
